import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import language from '../../variables/language';
import usePersistedState from '../../hooks/usePersistedState';

const RANGE_OPTIONS = ['7d', '30d'];

const bucketKeyFor = (dt) => dt.toFormat('yyyy-MM-dd');
const bucketLabelFor = (dt) => dt.toFormat('dd.MM');

function buildBuckets(range) {
   const now = DateTime.now();
   const steps = range === '7d' ? 6 : 29;
   const buckets = [];
   for (let i = steps; i >= 0; i--) {
      const dt = now.minus({ days: i }).startOf('day');
      buckets.push({ key: bucketKeyFor(dt), label: bucketLabelFor(dt), success: 0, fail: 0 });
   }
   return buckets;
}

// One row per actual classify/discover RUN (see WordCronRunStat on the
// backend), not per word or per request - already pre-aggregated, so
// bucketing here just sums two ints per day rather than counting statuses
// (that's the one real difference from ApiUsageChart, which this otherwise
// mirrors visually - same bar/legend/tooltip/table CSS classes reused
// directly since the "green success vs red fail, per day" shape is
// identical). `type` is the CronLogType to fetch ("WORD_CLASSIFICATION" or
// "WORD_DISCOVERY"); legend text comes from the caller so this stays
// agnostic to which of the two it's rendering. No internal chart title -
// the caller renders its own heading above this component (see
// vocabulary-config/Index.jsx), same split as ApiUsageChart/ai-config.
export default function WordCronEffectivenessChart({ type, chartKey, legendSuccess, legendFail }) {
   const langRoot = language[language.lang];
   const tChart = langRoot.wordCronEffectivenessChart;
   const tUsage = langRoot.apiUsageChart;

   const [rows, setRows] = useState([]);
   const [status, setStatus] = useState('loading');
   const [range, setRange] = usePersistedState('word-cron-effectiveness-range-' + chartKey, '30d');
   const [hovered, setHovered] = useState(null);
   const [showTable, setShowTable] = usePersistedState('word-cron-effectiveness-showTable-' + chartKey, false);

   useEffect(() => {
      setStatus('loading');
      axios.get('/api/word-cron-run-stats', { params: { type, limit: 3000 }, withCredentials: true })
         .then(response => {
            setRows(Array.isArray(response.data) ? response.data : []);
            setStatus('done');
         })
         .catch(() => setStatus('done'));
   }, [type]);

   const { buckets, stats } = useMemo(() => {
      const from = range === '7d' ? DateTime.now().minus({ days: 7 }) : DateTime.now().minus({ days: 30 });
      const bucketList = buildBuckets(range);
      const bucketMap = new Map(bucketList.map(b => [b.key, b]));

      let totalSuccess = 0, totalFail = 0;
      rows.forEach(row => {
         const dt = DateTime.fromISO(row.ranAt);
         if (!dt.isValid || dt < from) return;
         const bucket = bucketMap.get(bucketKeyFor(dt));
         if (bucket) {
            bucket.success += row.successCount;
            bucket.fail += row.failCount;
         }
         totalSuccess += row.successCount;
         totalFail += row.failCount;
      });

      const total = totalSuccess + totalFail;
      return {
         buckets: bucketList,
         stats: {
            total,
            successRate: total ? Math.round((totalSuccess / total) * 100) : null,
         },
      };
   }, [rows, range]);

   const maxCount = Math.max(1, ...buckets.map(b => b.success + b.fail));
   const labelStep = Math.max(1, Math.ceil(buckets.length / 8));

   return (
      <div className="vocab-config-tab-body">
         <div className="api-usage-header">
            <div className="api-usage-range-toggle">
               {RANGE_OPTIONS.map(r => (
                  <span key={r} className={r === range ? 'active' : ''} onClick={() => setRange(r)}>{tUsage['range' + r]}</span>
               ))}
            </div>
         </div>

         {status === 'loading' ? <p className="api-usage-empty">{langRoot['Loading ...']}</p> : ''}
         {status === 'done' && rows.length === 0 ? <p className="api-usage-empty">{tChart.noData}</p> : ''}

         {status === 'done' && rows.length > 0 ? (
            <>
               <div className="api-usage-stats">
                  <div className="api-usage-stat-tile">
                     <span className="api-usage-stat-value">{stats.total}</span>
                     <span className="api-usage-stat-label">{tChart.statTotal}</span>
                  </div>
                  <div className="api-usage-stat-tile">
                     <span className="api-usage-stat-value">{stats.successRate != null ? stats.successRate + '%' : '—'}</span>
                     <span className="api-usage-stat-label">{tChart.statSuccessRate}</span>
                  </div>
               </div>

               <div className="api-usage-chart-block">
                  <div className="api-usage-legend">
                     <span className="api-usage-legend-item"><i className="api-usage-swatch api-usage-swatch-success"></i>{legendSuccess}</span>
                     <span className="api-usage-legend-item"><i className="api-usage-swatch api-usage-swatch-error"></i>{legendFail}</span>
                  </div>
                  <div className="api-usage-bars-wrap">
                     <div className="api-usage-bars">
                        {buckets.map((b, i) => {
                           const totalB = b.success + b.fail;
                           return (
                              <div key={b.key} className="api-usage-bar-col" tabIndex={0}
                                 onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(prev => (prev === i ? null : prev))}
                                 onFocus={() => setHovered(i)} onBlur={() => setHovered(prev => (prev === i ? null : prev))}>
                                 {hovered === i ? (
                                    <div className="api-usage-tooltip">
                                       {b.label}<br />{legendSuccess}: <b>{b.success}</b><br />{legendFail}: <b>{b.fail}</b>
                                    </div>
                                 ) : ''}
                                 <div className="api-usage-bar-stack" style={{ height: ((totalB / maxCount) * 100) + '%' }}>
                                    {b.fail > 0 ? <div className="api-usage-bar-seg api-usage-seg-error" style={{ height: ((b.fail / totalB) * 100) + '%' }}></div> : ''}
                                    {b.success > 0 ? <div className="api-usage-bar-seg api-usage-seg-success" style={{ height: ((b.success / totalB) * 100) + '%' }}></div> : ''}
                                 </div>
                              </div>
                           );
                        })}
                     </div>
                     <div className="api-usage-bar-labels">
                        {buckets.map((b, i) => <span key={b.key}>{i % labelStep === 0 ? b.label : ''}</span>)}
                     </div>
                  </div>
               </div>

               <div className="api-usage-table-toggle" onClick={() => setShowTable(v => !v)}>
                  {showTable ? tUsage.hideTable : tUsage.showTable}
               </div>
               {showTable ? (
                  <div className="student-lessons-table-wrap">
                     <table className="student-lessons-table">
                        <thead>
                           <tr>
                              <th>{tChart.colDate}</th>
                              <th>{tChart.colSuccess}</th>
                              <th>{tChart.colFail}</th>
                              <th>{tChart.colRate}</th>
                           </tr>
                        </thead>
                        <tbody>
                           {buckets.slice().reverse().map(b => {
                              const totalB = b.success + b.fail;
                              const rate = totalB ? Math.round((b.success / totalB) * 100) : null;
                              return (
                                 <tr key={b.key}>
                                    <td>{b.label}</td>
                                    <td>{b.success}</td>
                                    <td>{b.fail}</td>
                                    <td>{rate != null ? rate + '%' : '—'}</td>
                                 </tr>
                              );
                           })}
                        </tbody>
                     </table>
                  </div>
               ) : ''}
            </>
         ) : ''}
      </div>
   );
}
